import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import ItemRenderer from "../items/ItemRenderer";
import ItemTypeGallery from "../items/ItemTypeGallery";
import { createNewItem, ITEM_TYPES } from "../../constants/itemTypes";

/**
 * A form component for creating and editing checklists
 *
 * @param {Object} props
 * @param {Object} props.checklist - Checklist data (null for new checklist)
 * @param {Function} props.onSave - Callback when form is submitted
 * @param {Function} props.onCancel - Callback when form is cancelled
 */
const ChecklistForm = ({ checklist = null, onSave, onCancel }) => {
  const isEditing = !!checklist;
  const [title, setTitle] = useState(checklist?.title || "");
  const [description, setDescription] = useState(checklist?.description || "");
  const [items, setItems] = useState(checklist?.items || []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const handleAddItem = (itemType) => {
    const newItem = createNewItem(itemType);
    setItems([...items, newItem]);
  };

  const handleUpdateItem = (id, updates) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const checklistData = {
      title: title.trim(),
      description: description.trim(),
      items,
      ...(isEditing && { id: checklist.id }),
    };

    onSave(checklistData);
  };

  const isFormValid = title.trim() && items.length > 0;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          {isEditing ? "Edit Checklist" : "Create New Checklist"}
        </Typography>

        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          value={title}
          onChange={handleTitleChange}
          required
          margin="normal"
        />

        <TextField
          fullWidth
          label="Description"
          variant="outlined"
          value={description}
          onChange={handleDescriptionChange}
          multiline
          rows={3}
          margin="normal"
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Checklist Items
        </Typography>

        {items.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {items.map((item) => (
              <ItemRenderer
                key={item.id}
                item={item}
                onUpdate={handleUpdateItem}
                onDelete={handleDeleteItem}
                isTemplateView={false}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            No items added yet. Add your first item below.
          </Typography>
        )}

        <ItemTypeGallery onAddItem={handleAddItem} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Button variant="outlined" onClick={onCancel} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!isFormValid}
            fullWidth={isMobile}
          >
            {isEditing ? "Save Changes" : "Create Checklist"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChecklistForm;
